<template>
  <div>
    <b-card>
      <breadcrumbs
        :breadcrumb-items="breadcrumbItems"
      />
      <DspPartnerToolbar
        v-if="Object.keys(dsppartner).length !== 0"
        :dsppartner="dsppartner"
        closing
      />
    </b-card>
    <StatsNavigation />
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-top">
            <h4>Статистика по RTB-партнёрам</h4>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              :value="rangePicker"
              :is-loading="isLoading"
              @refresh="handleRefresh"
              @input="toggleRange"
            >
              <template #additional-filters>
                <div class="search-input ml-2">
                  <b-input-group>
                    <b-form-input
                      v-model="searchQuery"
                      :placeholder="$t('form.search')"
                      @input="onSearch"
                    />
                    <b-input-group-append>
                      <b-button
                        variant="outline-primary"
                        @click="onSearch"
                      >
                        <feather-icon
                          icon="SearchIcon"
                        />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>
              </template>
            </StatsDateRangeFilter>
          </div>
        </div>
      </div>

      <div
        v-if="isLoading"
        class="text-center my-3"
      >
        <b-spinner label="Loading..." />
      </div>
      <b-table
        v-else-if="filteredRows.length"
        :items="filteredRows"
        :fields="fields"
        striped
        responsive
        small
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        show-empty
        foot-clone
        class="text-center"
      >
        <template #head(rtbPartner)>
          {{ $t('stats.rtbPartner') }}
        </template>
        <template #head(impressions)>
          {{ $t('stats.impressions') }}
        </template>
        <template #head(clicks)>
          {{ $t('stats.clicks') }}
        </template>
        <template #head(revenue)>
          {{ $t('stats.revenue') }}
        </template>
        <template #head(tax)>
          {{ $t('stats.tax') }}
        </template>
        <template #cell(rtbPartner)="{ item }">
          <router-link :to="{ name: 'dashboard-dsppartner-stats-rtb-partner-details', params: { rtbPartnerId: item.openrtb_partner_id } }">
            {{ partnerNameByIdFromStore(item.openrtb_partner_id) || item.openrtb_partner_id }}
          </router-link>
        </template>
        <template #cell(impressions)="data">
          <div class="d-flex align-items-center justify-content-center">
            <span style="position: relative;">
              {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
              <span
                v-if="getChange(data.value, getPreviousValue(data.item.openrtb_partner_id), 'impressions')"
                :class="[
                  'badge',
                  getChange(data.value, getPreviousValue(data.item.openrtb_partner_id), 'impressions').isPositive ? 'badge-success' : 'badge-danger'
                ]"
                style="font-size: 0.7rem;position: absolute;top: 0;left: 100%; margin-left: 5px;"
              >
                {{ getChange(data.value, getPreviousValue(data.item.openrtb_partner_id), 'impressions').formatted }}
              </span>
            </span>

          </div>
        </template>
        <template #cell(clicks)="data">
          <div class="d-flex align-items-center justify-content-center">
            <span style="position: relative;">
              {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
              <span
                v-if="getChange(data.value, getPreviousValue(data.item.openrtb_partner_id), 'clicks')"
                :class="[
                  'badge',
                  getChange(data.value, getPreviousValue(data.item.openrtb_partner_id), 'clicks').isPositive ? 'badge-success' : 'badge-danger'
                ]"
                style="font-size: 0.7rem;position: absolute;top: 0;left: 100%; margin-left: 5px;"
              >
                {{ getChange(data.value, getPreviousValue(data.item.openrtb_partner_id), 'clicks').formatted }}
              </span>
            </span>
          </div>
        </template>
        <template #cell(revenue)="data">
          <div class="d-flex align-items-center justify-content-center">
            <span style="position: relative;">
              {{ data.value ? currency('USD', data.value) : 0 }}
              <span
                v-if="getChange(data.value, getPreviousValue(data.item.openrtb_partner_id), 'revenue')"
                :class="[
                  'badge',
                  getChange(data.value, getPreviousValue(data.item.openrtb_partner_id), 'revenue').isPositive ? 'badge-success' : 'badge-danger'
                ]"
                style="font-size: 0.7rem;position: absolute;top: 0;left: 100%; margin-left: 5px;"
              >
                {{ getChange(data.value, getPreviousValue(data.item.openrtb_partner_id), 'revenue').formatted }}
              </span>
            </span>
          </div>
        </template>
        <template #cell(tax)="data">
          <div class="d-flex align-items-center justify-content-center">
            <span style="position: relative;">
              {{ data.value ? currency('USD', data.value) : 0 }}
              <span
                v-if="getChange(data.value, getPreviousValue(data.item.openrtb_partner_id), 'tax')"
                :class="[
                  'badge',
                  getChange(data.value, getPreviousValue(data.item.openrtb_partner_id), 'tax').isPositive ? 'badge-success' : 'badge-danger'
                ]"
                style="font-size: 0.7rem;position: absolute;top: 0;left: 100%; margin-left: 5px;"
              >
                {{ getChange(data.value, getPreviousValue(data.item.openrtb_partner_id), 'tax').formatted }}
              </span>
            </span>
          </div>
        </template>
        <template #cell(status)="data">
          <b-badge
            :variant="data.value !== 'active' ? 'light-success' : 'light-danger'"
          >
            {{ data.value !== 'active' ? 'Active' : 'Blocked' }}
          </b-badge>
        </template>
        <template #foot(rtbPartner)="data">
          <strong :data-label="data.label">{{ $t('Records') }}: {{ rows.length }}</strong>
        </template>
        <template #foot(impressions)>
          {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(sum('impressions')) }}
        </template>
        <template #foot(clicks)>
          {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(sum('clicks')) }}
        </template>
        <template #foot(revenue)>
          {{ currency('USD', sum('revenue')) }}
        </template>
        <template #foot(tax)>
          {{ currency('USD', sum('tax')) }}
        </template>
        <template #foot(status)>
          <span />
        </template>
      </b-table>
      <div
        v-else
        class="text-center text-muted my-3"
      >
        {{ $t('No data for this period') }}
      </div>
    </b-card>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import currencyFormatter from '@/utils/currency-formatter'
import getFormattedDate from '@/utils/date-formatter'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BCard,
  BTable,
  BButton,
  BBadge,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { mapGetters } from 'vuex'
import DspPartnerToolbar from '../DspPartnerToolbar.vue'
import StatsNavigation from './StatsNavigation.vue'
import StatsDateRangeFilter from './StatsDateRangeFilter.vue'

export default {
  name: 'RtbPartnerStats',
  components: {
    BCard,
    BTable,
    BButton,
    BBadge,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BSpinner,
    Breadcrumbs,
    DspPartnerToolbar,
    StatsNavigation,
    StatsDateRangeFilter,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      currency: currencyFormatter,
      rangePicker: [getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), getFormattedDate(new Date())],
      getFormattedDate,
      rows: [],
      previousRows: [], // Store previous values
      isLoading: false,
      searchQuery: '',
      sortBy: 'impressions',
      sortDesc: true,
      sortDirection: 'desc',
      showChanges: false, // Flag for showing changes
      fields: [
        { key: 'rtbPartner', label: 'RTB-партнёр', sortable: true },
        { key: 'impressions', label: 'Impressions', sortable: true },
        { key: 'clicks', label: 'Clicks', sortable: true },
        { key: 'revenue', label: 'Revenue', sortable: true },
        { key: 'tax', label: 'Tax', sortable: true },
      ],
      breadcrumbItems: [],
      dsppartner: {},
    }
  },
  computed: {
    ...mapGetters('dashboard-partners-stats', ['partnerNameByIdFromStore']),
    filteredRows() {
      if (!this.searchQuery) return this.rows
      const query = this.searchQuery.toLowerCase()
      return this.rows.filter(item => {
        const id = item.openrtb_partner_id
        const name = this.partnerNameByIdFromStore(id) || ''
        return name.toLowerCase().includes(query)
      })
    },
    selectedRangeFormated() {
      if (!this.rangePicker || !Array.isArray(this.rangePicker) || this.rangePicker.length !== 2) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Range Error',
            msg: 'Invalid date range format',
            variant: 'danger',
          },
          { root: true },
        )
        const today = getFormattedDate(new Date())
        return { from: today, to: today }
      }

      return {
        from: this.rangePicker[0],
        to: this.rangePicker[1],
      }
    },
  },
  created() {
    this.getPartnerData()

    // Priority: URL params > saved settings > default values
    const { from, to } = this.$route.query
    if (from && to) {
      // If URL has params, use them
      this.rangePicker = [from, to]
      this.getRtbPartnerStatsData() // Call only once during initialization
    } else {
      // Otherwise try to load saved settings
      this.loadSavedSettings()
      this.getRtbPartnerStatsData() // Call only once during initialization
    }

    // Reset values during initialization
    this.previousRows = []
    this.showChanges = false
  },
  methods: {
    // Calculate change for specific value
    getChange(currentValue, previousValue, key) {
      if (!this.showChanges || !previousValue) {
        return null
      }

      const current = currentValue || 0
      const previous = previousValue[key] || 0
      const change = current - previous

      if (change === 0) return null

      return {
        value: change,
        isPositive: change > 0,
        formatted: this.formatChange(change, key),
      }
    },

    // Format change based on data type
    formatChange(change, key) {
      if (key === 'revenue' || key === 'tax') {
        return `${change > 0 ? '+' : ''}${this.currency('USD', change)}`
      }
      return `${change > 0 ? '+' : ''}${new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(change)}`
    },

    // Find previous value by partner ID
    getPreviousValue(partnerId) {
      return this.previousRows.find(item => item.openrtb_partner_id === partnerId)
    },
    async getPartnerData() {
      const responseData = await useJwt.getDsppartner(this.$route.params.id)
      this.dsppartner = responseData.data.dsppartner || {}
      this.setBreabcrumbs()
    },
    setBreabcrumbs() {
      this.breadcrumbItems = [
        {
          text: this.$t('DSP partners'),
          to: '/dsppartners/',
        },
        {
          text: this.dsppartner.name,
          to: `/dsppartners/${this.dsppartner._id}/`,
        },
        {
          text: this.$t('Statistics'),
          active: true,
        },
      ]
    },
    async getRtbPartnerStatsData() {
      this.isLoading = true
      const { from, to } = this.selectedRangeFormated
      try {
        const response = await useJwt.getDsppartnerStatsRtbTotal(
          this.$route.params.id,
          {
            from,
            to,
            type: 'daily',
          },
        )
        const items = response.data.items || []

        this.rows = items.map(item => ({ ...item }))
        // Collect all unique openrtb_partner_id and load names
        const ids = [...new Set(items.map(item => item.openrtb_partner_id).filter(Boolean))]
        if (ids.length) {
          await this.$store.dispatch('dashboard-partners-stats/fetchPartnerNamesById', ids)
        }
      } catch (error) {
        this.rows = []
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message,
            variant: 'danger',
          },
          { root: true },
        )
      } finally {
        this.isLoading = false
      }
    },
    sum(key) {
      return this.rows.reduce((acc, r) => acc + (r[key] || 0), 0)
    },
    toggleRange(selectedDates) {
      if (!selectedDates || !Array.isArray(selectedDates) || selectedDates.length !== 2) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Range Error',
            msg: 'Invalid date range format',
            variant: 'danger',
          },
          { root: true },
        )
        return
      }

      const [fromDate, toDate] = selectedDates
      const from = getFormattedDate(new Date(fromDate))
      const to = getFormattedDate(new Date(toDate))

      // Only update if we have valid dates
      if (from !== 'Invalid date' && to !== 'Invalid date') {
        // Check if dates actually changed
        if (this.rangePicker[0] === from && this.rangePicker[1] === to) {
          return
        }

        // Reset previous values when changing date range
        this.previousRows = []
        this.showChanges = false

        // Update state and URL in one go
        this.rangePicker = [from, to]
        // Update URL without page reload
        const newQuery = { ...this.$route.query, from, to }
        const newPath = this.$route.path
        window.history.replaceState(
          {},
          '',
          `${newPath}?${new URLSearchParams(newQuery).toString()}`,
        )
        this.currentPage = 1 // Reset to first page when changing date range
        this.getRtbPartnerStatsData()
      } else {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Format Error',
            msg: 'Invalid date format',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    onSearch() {
      // Do nothing, filtering through computed
    },
    handleRefresh() {
      // Force save current values before update
      if (this.rows.length > 0) {
        this.previousRows = [...this.rows]
        this.showChanges = true
      }
      this.getRtbPartnerStatsData()
    },
    async blockRtbPartner(item) {
      // TODO: Implement API call to block RTB partner
      item.status = 'blocked'
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Success',
          icon: 'CheckIcon',
          variant: 'success',
          text: 'RTB partner has been blocked successfully',
        },
      })
    },
    async unblockRtbPartner(item) {
      // TODO: Implement API call to unblock RTB partner
      item.status = 'active'
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Success',
          icon: 'CheckIcon',
          variant: 'success',
          text: 'RTB partner has been unblocked successfully',
        },
      })
    },
    loadSavedSettings() {
      try {
        const saved = localStorage.getItem('dspStatsDateFilter')
        if (saved) {
          const settings = JSON.parse(saved)
          if (settings.rangePicker && !this.$route.query.from && !this.$route.query.to) {
            this.rangePicker = settings.rangePicker
          }
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Settings Error',
            msg: 'Failed to load date filter settings',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style scoped lang="scss">
.card-statistics .card-body {
  min-height: 190px;
}

.card-statistics-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.search-input {
  min-width: 200px;
}

// Animation for showing changes
.badge {
  animation: fadeInScale 0.3s ease-in-out;
}

@keyframes fadeInScale {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@media (max-width: 440px) {
  [dir] #app {
    .card-statistics-tools {
      flex-wrap: wrap;
    }
  }
}
</style>
